/**
 * Observes GameDistribution SDK ad events by wrapping `window.GD_OPTIONS.onEvent`.
 *
 * The wrapping MUST be installed before the azerion-bridge runs `bridge.init()`
 * (which assigns `window.GD_OPTIONS` and injects the GD SDK script). The SDK reads
 * `onEvent` by value at load time, so a later wrapper (e.g. from the ads module
 * constructor) is never invoked. We install an accessor on `window.GD_OPTIONS` so we
 * can wrap `onEvent` at the exact moment the bridge assigns the options object.
 *
 * This lets the game detect a skipped rewarded ad, which the bridge otherwise reports
 * as `finished: true` (it resolves on ALL_ADS_COMPLETED, which also fires on skip).
 */

interface GdOptions {
  onEvent?: (event: { name?: string }) => void;
}

const wrapped = new WeakSet<object>();
const state = { skipped: false };
let installed = false;

function wrapOnEvent(options: GdOptions | undefined): void {
  if (!options || typeof options.onEvent !== 'function') return;
  if (wrapped.has(options)) return;
  wrapped.add(options);

  const original = options.onEvent;
  options.onEvent = (event: { name?: string }): void => {
    if (event.name === 'SKIPPED') {
      state.skipped = true;
    }
    original(event);
  };
}

/**
 * Installs the GD_OPTIONS accessor. Call once, before `bridge.init()`.
 */
export function installGdAdEventObserver(): void {
  if (installed) return;
  installed = true;

  const globalWindow = window as unknown as {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    GD_OPTIONS?: GdOptions;
  };
  let current = globalWindow.GD_OPTIONS;

  // Wrap it if the bridge already set it (defensive; normally it hasn't yet).
  wrapOnEvent(current);

  try {
    Object.defineProperty(globalWindow, 'GD_OPTIONS', {
      configurable: true,
      get: () => current,
      set: (value: GdOptions | undefined) => {
        wrapOnEvent(value);
        current = value;
      },
    });
  } catch {
    // If the property can't be redefined we simply lose skip detection; never throw.
  }
}

/** Clears the skip flag at the start of an ad. */
export function resetAdSkipped(): void {
  state.skipped = false;
}

/** True if a SKIPPED event was observed during the current ad. */
export function wasAdSkipped(): boolean {
  return state.skipped;
}
